import { useMemo, useState } from "react";
import { useInfiniteQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import { queryKeys } from "@nasam/kernel";
import { useDebouncedValue } from "../../../shared/application/useDebouncedValue";
import type { DepartmentsListQuery, DepartmentsRepository } from "../domain";

const PAGE_SIZE = 20;
const SEARCH_DEBOUNCE_MS = 300;

export interface DepartmentOption {
  readonly value: string;
  readonly label: string;
}

export interface UseDepartmentOptionsResult {
  readonly options: readonly DepartmentOption[];
  readonly searchValue: string;
  readonly onSearchChange: (value: string) => void;
  readonly isLoading: boolean;
  readonly isFetchingMore: boolean;
  readonly hasMore: boolean;
  readonly onLoadMore: () => void;
}

/**
 * Server-searched, paginated department options for the `Combobox` async
 * mode -- mirrors useBranchOptions/useCompanyOptions. `repository` is
 * optional to match call sites where the organization feature isn't
 * registered.
 */
export function useDepartmentOptions(
  repository: DepartmentsRepository | undefined,
): UseDepartmentOptionsResult {
  const { i18n } = useTranslation();
  const isAr = i18n.language.startsWith("ar");
  const sortBy = isAr ? "nameAr" : "nameEn";

  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearch = useDebouncedValue(searchTerm, SEARCH_DEBOUNCE_MS);
  const search =
    debouncedSearch.trim() === "" ? undefined : debouncedSearch.trim();

  const isEnabled =
    repository !== undefined && typeof repository.list === "function";

  const listQuery = (page: number): DepartmentsListQuery => ({
    page,
    limit: PAGE_SIZE,
    sort: "asc",
    sortBy,
    ...(search === undefined ? {} : { search }),
  });

  const query = useInfiniteQuery({
    queryKey: queryKeys.departments.list({
      limit: PAGE_SIZE,
      search,
      sort: "asc",
      sortBy,
    }),
    queryFn: ({ pageParam }) => repository!.list(listQuery(pageParam)),
    initialPageParam: 1,
    getNextPageParam: (lastPage) =>
      lastPage.meta.currentPage < lastPage.meta.totalPages
        ? lastPage.meta.currentPage + 1
        : undefined,
    enabled: isEnabled,
  });

  const options = useMemo(
    () =>
      (query.data?.pages ?? []).flatMap((page) =>
        page.data.map((department) => ({
          value: department.id,
          label: isAr
            ? department.nameAr || department.nameEn
            : department.nameEn || department.nameAr,
        })),
      ),
    [query.data, isAr],
  );

  return {
    options,
    searchValue: searchTerm,
    onSearchChange: setSearchTerm,
    isLoading: query.isLoading,
    isFetchingMore: query.isFetchingNextPage,
    hasMore: query.hasNextPage,
    onLoadMore: () => {
      void query.fetchNextPage();
    },
  };
}
